import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import type { OrganizationContactRole } from "../../../../api/types/userManagementTypes";
import { organizationContactRoleCaptionsMap } from "../../../../utilities/enumCaptions";
import OrganizationContactRow from "./OrganizationContactRow";
import type { OrganizationContactRoleInfo } from "./organizationContactRoles";
import type { RoleContactGroup } from "./organizationContactsState";

interface Props {
  roleInfo: OrganizationContactRoleInfo;
  group: RoleContactGroup;
  canEdit: boolean;
  onAddContact: (role: OrganizationContactRole) => void;
  onRemoveMember: (role: OrganizationContactRole, userId: string) => void;
  onRemoveExternal: (role: OrganizationContactRole, email: string) => void;
}

const OrganizationContactRoleSection = ({
  roleInfo,
  group,
  canEdit,
  onAddContact,
  onRemoveMember,
  onRemoveExternal,
}: Props) => {
  const { role, description, maxCount } = roleInfo;
  const contactCount = group.members.length + group.externals.length;

  const isRemovalDisabled = contactCount <= 1;

  return (
    <Stack direction="row" spacing={6.5} sx={{ py: 5 }}>
      <Stack spacing={0.5} sx={{ flex: "1 1 50%" }}>
        <Typography variant="subtitle1">
          {organizationContactRoleCaptionsMap[role] ?? role}
          <RequiredFieldMarker />
        </Typography>
        <Typography color="textSecondary">{description}</Typography>
      </Stack>
      <Stack spacing={1.5} sx={{ flex: "1 1 50%", minWidth: 0 }}>
        {contactCount === 0 && <Alert severity="error">At least one assignee must be added.</Alert>}
        {contactCount > 0 && (
          <>
            {group.members.map((member) => (
              <OrganizationContactRow
                key={member.userId}
                name={member.name}
                email={member.email}
                onRemove={canEdit ? () => onRemoveMember(role, member.userId) : undefined}
                removeDisabled={isRemovalDisabled}
              />
            ))}
            {group.externals.map((external) => (
              <OrganizationContactRow
                key={external.email}
                name={external.name}
                email={external.email}
                onRemove={canEdit ? () => onRemoveExternal(role, external.email) : undefined}
                removeDisabled={isRemovalDisabled}
              />
            ))}
          </>
        )}

        {canEdit && contactCount < maxCount && (
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => onAddContact(role)}
            >
              Add Contact
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default OrganizationContactRoleSection;
